import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Building2, Plus, Eye, FileText, CheckCircle2, XCircle,
  MapPin, Bed, Bath, Calendar, LogOut, Home, Settings,
  Clock, ThumbsUp, ThumbsDown, X, MessageSquare, Send, ArrowLeft, HelpCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { marketplaceAPI, messagesAPI } from '../services/api';
import FAQFeedback from '../components/FAQFeedback';
import RoleSwitcher from '../components/RoleSwitcher';
import ListingDetailModal from '../components/ListingDetailModal';
import MessagingPanel from '../components/MessagingPanel';
import ReferralDashboard from '../components/ReferralDashboard';
import FoundingCohortBadge from '../components/FoundingCohortBadge';
import { Sparkles } from 'lucide-react';

const LandlordDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, getAuthToken, isAuthenticated, loading: authLoading } = useAuth();
  const { toast } = useToast();
  
  const [activeTab, setActiveTab] = useState('listings');
  const [dashboard, setDashboard] = useState(null);
  const [listings, setListings] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddListing, setShowAddListing] = useState(false);
  const [selectedListing, setSelectedListing] = useState(null);
  const [landlordUnread, setLandlordUnread] = useState(0);
  // Acknowledged state so the red Messages badge clears as soon as the
  // user opens the Messages tab, only reappearing on a genuinely new
  // message (next poll detects count increase).
  const [messagesAck, setMessagesAck] = useState(false);
  const prevLandlordUnreadRef = useRef(0);
  const [newOffersCount, setNewOffersCount] = useState(0);
  const [selectedOfferForChat, setSelectedOfferForChat] = useState(null);
  const prevPendingOffersRef = React.useRef(null); // tracks last known pending count
  const [newListing, setNewListing] = useState({
    address: '', city: '', postcode: '', bedrooms: 1, bathrooms: 1,
    monthlyRent: 1000, deposit: 1000, utilitiesIncluded: false,
    description: '', propertyType: 'apartment', availableFrom: '',
    minTerm: 12, petFriendly: false, furnished: true
  });

  useEffect(() => {
    if (authLoading) return; // Wait for auth to finish loading
    if (!isAuthenticated()) {
      navigate('/login', { replace: true });
      return;
    }

    if (!user?.roles?.includes('landlord')) {
      toast({
        title: 'Access Denied',
        description: 'You must be a registered landlord to access this page',
        variant: 'destructive'
      });
      navigate('/', { replace: true });
      return;
    }

    fetchData();
  }, [authLoading, isAuthenticated, user, navigate]);

  const fetchLandlordUnread = useCallback(async () => {
    try {
      const token = getAuthToken();
      const data = await messagesAPI.getUnreadCount(token);
      const newCount = data?.landlordUnread || 0;
      // If a genuinely new message arrived since last poll, un-acknowledge
      // so the badge reappears.
      if (newCount > prevLandlordUnreadRef.current) {
        setMessagesAck(false);
      }
      prevLandlordUnreadRef.current = newCount;
      setLandlordUnread(newCount);
    } catch {}
  }, [getAuthToken]);

  // Acknowledge unread badge the moment the user opens the Messages tab.
  useEffect(() => {
    if (activeTab === 'messages') {
      setMessagesAck(true);
      setLandlordUnread(0);
      prevLandlordUnreadRef.current = 0;
    }
  }, [activeTab]);

  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      fetchLandlordUnread();
      const interval = setInterval(fetchLandlordUnread, 30000);
      return () => clearInterval(interval);
    }
  }, [authLoading, isAuthenticated, fetchLandlordUnread]);

  const pollOffers = useCallback(async () => {
    try {
      const token = getAuthToken();
      const fresh = await marketplaceAPI.getReceivedOffers(token);
      setOffers(fresh);
      const freshPending = fresh.filter(o => o.status === 'pending').length;
      if (prevPendingOffersRef.current !== null && freshPending > prevPendingOffersRef.current) {
        const diff = freshPending - prevPendingOffersRef.current;
        setNewOffersCount(prev => prev + diff);
        toast({
          title: '🎉 New Offer Received!',
          description: `You have ${diff} new offer${diff > 1 ? 's' : ''} waiting for your review.`,
        });
      }
      prevPendingOffersRef.current = freshPending;
    } catch {}
  }, [getAuthToken, toast]);

  useEffect(() => {
    if (!authLoading && isAuthenticated()) {
      const interval = setInterval(pollOffers, 30000);
      return () => clearInterval(interval);
    }
  }, [authLoading, isAuthenticated, pollOffers]);

  const fetchData = async () => {
    try {
      const token = getAuthToken();
      const [dashboardData, listingsData, offersData] = await Promise.all([
        marketplaceAPI.getLandlordDashboard(token),
        marketplaceAPI.getMyListings(token),
        marketplaceAPI.getReceivedOffers(token)
      ]);
      setDashboard(dashboardData);
      setListings(listingsData);
      setOffers(offersData);
      // Seed baseline so polling doesn't fire on first load
      prevPendingOffersRef.current = offersData.filter(o => o.status === 'pending').length;
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({ title: 'Error', description: 'Failed to load dashboard', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleListingUpdated = (updatedListing) => {
    setListings(prev => prev.map(l => l._id === updatedListing._id ? updatedListing : l));
    setSelectedListing(updatedListing);
  };

  const handleCreateListing = async (e) => {
    e.preventDefault();
    try {
      const token = getAuthToken();
      await marketplaceAPI.createListing(newListing, token);
      toast({ title: 'Success', description: 'Listing created successfully' });
      setShowAddListing(false);
      setNewListing({
        address: '', city: '', postcode: '', bedrooms: 1, bathrooms: 1,
        monthlyRent: 1000, deposit: 1000, utilitiesIncluded: false,
        description: '', propertyType: 'apartment', availableFrom: '',
        minTerm: 12, petFriendly: false, furnished: true
      });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to create listing', variant: 'destructive' });
    }
  };

  const handleAcceptOffer = async (offerId) => {
    const token = getAuthToken();

    // Show the landlord what they will actually receive BEFORE they accept.
    // Ariv's 6% success fee is deducted from the rent they agreed with the
    // host, so the figure that reaches them is lower than the one they
    // negotiated. Finding that out when the transfer arrives would be our
    // failure, not theirs. The numbers come from the server, from the same
    // function that builds the charge and settles the deal.
    let confirmMessage =
      'Are you sure you want to accept this offer? Other pending offers will be rejected.';
    try {
      const b = await marketplaceAPI.getDealBreakdown(offerId, token);
      const money = (n) => `£${Number(n).toFixed(2)}`;
      confirmMessage =
        `Rent agreed: ${money(b.landlord.agreedRent)}\n` +
        `Ariv success fee (6%): −${money(b.landlord.platformFee)}\n` +
        `You receive: ${money(b.landlord.totalToReceive)}\n\n` +
        'This fee is charged once, on this deal only. Ariv takes nothing from ' +
        'your rent after this.\n\n' +
        'Accept this offer? Other pending offers will be rejected.';
    } catch (e) {
      // If the breakdown cannot be loaded we must not quietly fall back to a
      // confirmation that hides the fee entirely.
      confirmMessage =
        'Ariv charges the landlord a 6% success fee on this deal, deducted ' +
        'once from the rent you agreed. We could not load the exact figures ' +
        'just now.\n\nAccept this offer? Other pending offers will be rejected.';
    }

    if (!window.confirm(confirmMessage)) return;
    try {
      await marketplaceAPI.acceptOffer(offerId, token);
      toast({ title: 'Success', description: 'Offer accepted!' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to accept offer', variant: 'destructive' });
    }
  };
  const handleRejectOffer = async (offerId) => {
    try {
      const token = getAuthToken();
      await marketplaceAPI.rejectOffer(offerId, { reason: 'Offer not suitable' }, token);
      toast({ title: 'Offer Rejected', description: 'The offer has been rejected' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to reject offer', variant: 'destructive' });
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'under_offer': return 'bg-amber-100 text-amber-800';
      case 'closed': return 'bg-gray-100 text-gray-800';
      case 'pending': return 'bg-blue-100 text-blue-800';
      case 'accepted': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F2E9] flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68] mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Header */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
              <span className="text-lg font-semibold text-[#1F3A68]">Landlord Portal</span>
            </div>
            <div className="flex items-center gap-3">
              <RoleSwitcher currentPortal="landlord" />
              <Button variant="outline" size="sm" onClick={() => { logout(); navigate('/'); }} aria-label="Logout">
                <LogOut size={16} className="sm:mr-1" /> <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Building2 size={32} />
                <div>
                  <div className="text-3xl font-bold">{dashboard?.stats?.activeListings || 0}</div>
                  <div className="text-green-100">Active Listings</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <Clock size={32} />
                <div>
                  <div className="text-3xl font-bold">{dashboard?.stats?.underOffer || 0}</div>
                  <div className="text-amber-100">Under Offer</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-[#1F3A68] to-[#2d4f8a] text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <CheckCircle2 size={32} />
                <div>
                  <div className="text-3xl font-bold">{dashboard?.stats?.closedDeals || 0}</div>
                  <div className="text-blue-100">Closed Deals</div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
            <CardContent className="p-6">
              <div className="flex items-center gap-3">
                <FileText size={32} />
                <div>
                  <div className="text-3xl font-bold">{dashboard?.stats?.pendingOffers || 0}</div>
                  <div className="text-purple-100">Pending Offers</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={(tab) => {
          setActiveTab(tab);
          if (tab === 'offers') setNewOffersCount(0);
        }}>
          {activeTab !== 'messages' && (
            <div className="flex justify-end mb-3">
              <Button
                className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                onClick={() => setShowAddListing(true)}
                data-testid="add-listing-btn"
              >
                <Plus size={16} className="mr-1" /> Add Listing
              </Button>
            </div>
          )}
          <TabsList className="flex flex-wrap justify-center w-full bg-white px-3 sm:px-4 py-2 rounded-xl shadow-md gap-1.5 mb-6">
            <TabsTrigger value="listings" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="landlord-listings-tab">
              <Building2 size={14} className="mr-1 hidden sm:inline" />
              My Listings ({listings.length})
            </TabsTrigger>
            <TabsTrigger value="offers" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg relative whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="landlord-offers-tab">
              <FileText size={14} className="mr-1 hidden sm:inline" />
              Offers ({offers.filter(o => o.status === 'pending').length} pending)
              {newOffersCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#CB3B3B] text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {newOffersCount > 9 ? '9+' : newOffersCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="messages" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg relative whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="landlord-messages-tab">
              <MessageSquare size={14} className="mr-1 hidden sm:inline" />
              Messages
              {landlordUnread > 0 && !messagesAck && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#CB3B3B] text-white text-xs rounded-full flex items-center justify-center font-bold">
                  {landlordUnread > 9 ? '9+' : landlordUnread}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="referrals" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="landlord-referrals-tab">
              <Sparkles size={14} className="mr-1 hidden sm:inline" />
              Refer &amp; Earn
            </TabsTrigger>
            <TabsTrigger value="faq" className="px-3 py-1.5 text-xs sm:text-sm data-[state=active]:bg-[#1F3A68] data-[state=active]:text-white rounded-lg whitespace-normal text-center leading-tight max-w-[110px] sm:max-w-none" data-testid="landlord-faq-tab">
              <HelpCircle size={14} className="mr-1 hidden sm:inline" />
              FAQ
            </TabsTrigger>
          </TabsList>

          {/* Listings Tab */}
          <TabsContent value="listings">
            {listings.length === 0 ? (
              <Card className="p-8 text-center">
                <Building2 size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700">No listings yet</h3>
                <p className="text-gray-500 mb-4">Create your first rental listing to start receiving offers</p>
                <Button 
                  className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                  onClick={() => setShowAddListing(true)}
                >
                  <Plus size={16} className="mr-1" /> Add Listing
                </Button>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {listings.map(listing => (
                  <ListingCard
                    key={listing._id}
                    listing={listing}
                    getStatusBadge={getStatusBadge}
                    onClick={() => setSelectedListing(listing)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Offers Tab */}
          <TabsContent value="offers">
            {offers.length === 0 ? (
              <Card className="p-8 text-center">
                <FileText size={48} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-gray-700">No offers yet</h3>
                <p className="text-gray-500">Offers from hosts will appear here</p>
              </Card>
            ) : (
              <div className="space-y-4">
                {offers.map(offer => (
                  <OfferCard
                    key={offer._id}
                    offer={offer}
                    onAccept={() => handleAcceptOffer(offer._id)}
                    onReject={() => handleRejectOffer(offer._id)}
                    getStatusBadge={getStatusBadge}
                    onMessageHost={(o) => setSelectedOfferForChat(o)}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <MessagingPanel
              userRole="landlord"
              onUnreadChange={fetchLandlordUnread}
            />
          </TabsContent>

          <TabsContent value="referrals">
            <ReferralDashboard />
          </TabsContent>

          <TabsContent value="faq">
            <FAQFeedback />
          </TabsContent>
        </Tabs>
      </main>

      {/* Add Listing Modal */}
      {showAddListing && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <Card className="w-full max-w-2xl my-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Add New Listing</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowAddListing(false)}>
                <X size={20} />
              </Button>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreateListing} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium">Address *</label>
                    <Input 
                      value={newListing.address}
                      onChange={(e) => setNewListing({...newListing, address: e.target.value})}
                      placeholder="123 Main Street"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">City *</label>
                    <Input 
                      value={newListing.city}
                      onChange={(e) => setNewListing({...newListing, city: e.target.value})}
                      placeholder="London"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Postcode *</label>
                    <Input 
                      value={newListing.postcode}
                      onChange={(e) => setNewListing({...newListing, postcode: e.target.value})}
                      placeholder="SW1A 1AA"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Property Type</label>
                    <select 
                      value={newListing.propertyType}
                      onChange={(e) => setNewListing({...newListing, propertyType: e.target.value})}
                      className="w-full h-10 border rounded-md px-3"
                    >
                      <option value="apartment">Apartment</option>
                      <option value="house">House</option>
                      <option value="studio">Studio</option>
                      <option value="room">Room</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Bedrooms</label>
                    <Input 
                      type="number"
                      min="0"
                      value={newListing.bedrooms}
                      onChange={(e) => setNewListing({...newListing, bedrooms: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Bathrooms</label>
                    <Input 
                      type="number"
                      min="1"
                      value={newListing.bathrooms}
                      onChange={(e) => setNewListing({...newListing, bathrooms: parseInt(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Monthly Rent (£) *</label>
                    <Input 
                      type="number"
                      min="0"
                      value={newListing.monthlyRent}
                      onChange={(e) => setNewListing({...newListing, monthlyRent: parseFloat(e.target.value) || 0})}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Deposit (£)</label>
                    <Input 
                      type="number"
                      min="0"
                      value={newListing.deposit}
                      onChange={(e) => setNewListing({...newListing, deposit: parseFloat(e.target.value) || 0})}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Available From *</label>
                    <Input 
                      type="date"
                      value={newListing.availableFrom}
                      onChange={(e) => setNewListing({...newListing, availableFrom: e.target.value})}
                      required
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Min Term (months)</label>
                    <Input 
                      type="number"
                      min="1"
                      value={newListing.minTerm}
                      onChange={(e) => setNewListing({...newListing, minTerm: parseInt(e.target.value) || 0})}
                    />
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-medium">Description *</label>
                  <Textarea 
                    value={newListing.description}
                    onChange={(e) => setNewListing({...newListing, description: e.target.value})}
                    placeholder="Describe your property..."
                    rows={4}
                    required
                  />
                </div>
                
                <div className="flex flex-wrap gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={newListing.utilitiesIncluded}
                      onChange={(e) => setNewListing({...newListing, utilitiesIncluded: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>Utilities Included</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={newListing.furnished}
                      onChange={(e) => setNewListing({...newListing, furnished: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>Furnished</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input 
                      type="checkbox"
                      checked={newListing.petFriendly}
                      onChange={(e) => setNewListing({...newListing, petFriendly: e.target.checked})}
                      className="w-4 h-4"
                    />
                    <span>Pet Friendly</span>
                  </label>
                </div>
                
                <div className="flex gap-3 pt-4">
                  <Button type="submit" className="flex-1 bg-[#1F3A68] hover:bg-[#1F3A68]/90">
                    Create Listing
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowAddListing(false)}>
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {selectedListing && (
        <ListingDetailModal
          listing={selectedListing}
          mode="landlord"
          onClose={() => setSelectedListing(null)}
          onUpdate={handleListingUpdated}
        />
      )}

      {selectedOfferForChat && (
        <DealChatModal
          offer={selectedOfferForChat}
          onClose={() => setSelectedOfferForChat(null)}
        />
      )}
    </div>
  );
};

// Listing Card Component
const ListingCard = ({ listing, getStatusBadge, onClick }) => (
  <Card className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" onClick={onClick} data-testid={`listing-card-${listing._id}`}>
    <div className="h-40 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
      {listing.images?.[0] ? (
        <img src={listing.images[0]} alt={listing.address} className="w-full h-full object-cover" />
      ) : (
        <Building2 size={48} className="text-gray-400" />
      )}
    </div>
    <CardContent className="p-4">
      <div className="flex justify-between items-start mb-2">
        <h3 className="font-semibold text-lg">{listing.address}</h3>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(listing.status)}`}>
          {listing.status}
        </span>
      </div>
      <p className="text-gray-600 flex items-center gap-1 text-sm mb-2">
        <MapPin size={14} /> {listing.city}, {listing.postcode}
      </p>
      <div className="flex gap-4 text-sm text-gray-500 mb-3">
        <span className="flex items-center gap-1"><Bed size={14} /> {listing.bedrooms}</span>
        <span className="flex items-center gap-1"><Bath size={14} /> {listing.bathrooms}</span>
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xl font-bold text-[#1F3A68]">£{listing.monthlyRent}/mo</span>
        <span className="text-sm text-gray-500">{listing.views || 0} views</span>
      </div>
    </CardContent>
  </Card>
);

// Offer Card Component
const OfferCard = ({ offer, onAccept, onReject, getStatusBadge, onMessageHost }) => (
  <Card className="hover:shadow-lg transition-shadow" data-testid={`offer-card-${offer._id}`}>
    <CardContent className="p-6">
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold">{offer.listingAddress}</h3>
            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(offer.status)}`}>
              {offer.status}
            </span>
          </div>
          <p className="text-gray-600 text-sm mb-3">{offer.listingCity}</p>

          <div className="grid md:grid-cols-3 gap-4 p-3 bg-gray-50 rounded-lg mb-3">
            <div>
              <div className="text-sm text-gray-500">Original Rent</div>
              <div className="font-semibold">£{offer.originalRent}/mo</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Proposed Rent</div>
              <div className="font-semibold text-[#1F3A68]">£{offer.proposedRent}/mo</div>
            </div>
            <div>
              <div className="text-sm text-gray-500">Term</div>
              <div className="font-semibold">{offer.proposedTerm} months</div>
            </div>
          </div>

          <div className="mb-3">
            <div className="text-sm text-gray-500">From: {offer.hostName} ({offer.hostEmail})</div>
            {offer.message && (
              <div className="mt-2 p-3 bg-blue-50 rounded-lg text-sm italic">
                "{offer.message}"
              </div>
            )}
          </div>

          {/* Message Host button */}
          <Button
            size="sm"
            variant="outline"
            className="border-[#1F3A68] text-[#1F3A68] hover:bg-[#1F3A68]/5"
            onClick={() => onMessageHost(offer)}
          >
            <MessageSquare size={14} className="mr-1.5" /> Message Host
          </Button>
        </div>

        {offer.status === 'pending' && (
          <div className="flex flex-col gap-2 ml-4">
            <Button size="sm" className="bg-green-600 hover:bg-green-700" onClick={onAccept}>
              <ThumbsUp size={14} className="mr-1" /> Accept
            </Button>
            <Button size="sm" variant="outline" className="text-red-600" onClick={onReject}>
              <ThumbsDown size={14} className="mr-1" /> Reject
            </Button>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// Deal Chat Modal — for landlord replying to host offers
const DealChatModal = ({ offer, onClose }) => {
  const { getAuthToken, user } = useAuth();
  const { toast } = useToast();
  const [conversation, setConversation] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatSending, setChatSending] = useState(false);
  const [chatLoading, setChatLoading] = useState(true);
  const chatPollRef = useRef(null);
  const chatEndRef = useRef(null);
  const currentUserId = user?._id || user?.id;

  // Use a ref to track the current conversation id for polling without stale closure
  const conversationIdRef = useRef(null);

  const loadConversation = async (showSpinner = false) => {
    if (showSpinner) setChatLoading(true);
    try {
      const token = getAuthToken();
      const convs = await messagesAPI.getConversations(token, 'landlord');
      // Exclude self-conversations (stale data where hostId === guestId)
      const match = convs.find(c =>
        c.conversationType === 'deal' &&
        c.relatedId === offer.rentalListingId &&
        String(c.hostId) !== String(c.guestId)
      );
      if (match) {
        conversationIdRef.current = match._id;
        setConversation(match);
        const data = await messagesAPI.getConversation(match._id, token);
        setChatMessages(data.messages || []);
      }
    } catch (err) {
      console.error('loadConversation error', err);
    } finally {
      if (showSpinner) setChatLoading(false);
    }
  };

  useEffect(() => {
    // Initial load with spinner, then always poll every 5s (discovers new convs too)
    loadConversation(true);
    chatPollRef.current = setInterval(() => loadConversation(false), 5000);
    return () => clearInterval(chatPollRef.current);
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  const handleSendMessage = async () => {
    if (!chatInput.trim()) return;
    setChatSending(true);
    const msgText = chatInput.trim();
    setChatInput('');
    try {
      const token = getAuthToken();
      if (!conversation) {
        // Landlord initiates — pass landlordId so backend assigns correct hostId/guestId
        const conv = await messagesAPI.createConversation({
          participantId: offer.hostId,
          conversationType: 'deal',
          participantType: 'landlord',
          initialMessage: msgText,
          relatedId: offer.rentalListingId,
          landlordId: currentUserId,
        }, token);
        conversationIdRef.current = conv._id;
        setConversation(conv);
        const data = await messagesAPI.getConversation(conv._id, token);
        setChatMessages(data.messages || []);
        // Polling already running from useEffect — no need to start another interval
      } else {
        const msg = await messagesAPI.sendMessage({
          conversationId: conversation._id,
          content: msgText,
        }, token);
        setChatMessages(prev => [...prev, msg]);
      }
    } catch (err) {
      setChatInput(msgText);
      toast({ title: 'Error', description: 'Failed to send message', variant: 'destructive' });
    } finally {
      setChatSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="w-full max-w-lg bg-white rounded-xl shadow-2xl flex flex-col"
        style={{ height: '80vh', maxHeight: '600px' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b flex items-center justify-between bg-[#1F3A68] rounded-t-xl flex-shrink-0">
          <div>
            <div className="flex items-center gap-2">
              <MessageSquare size={18} className="text-white" />
              <h3 className="font-bold text-white text-base">Message Host</h3>
            </div>
            <p className="text-blue-200 text-xs mt-0.5">{offer.listingAddress} · {offer.hostName}</p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white p-1 rounded-lg transition-colors">
            <X size={20} />
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-gray-50">
          {chatLoading && (
            <div className="text-center text-gray-400 text-sm py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#1F3A68] mx-auto mb-2" />
              Loading conversation…
            </div>
          )}
          {!chatLoading && chatMessages.length === 0 && (
            <div className="text-center py-10">
              <MessageSquare size={36} className="text-gray-300 mx-auto mb-2" />
              <p className="text-gray-500 text-sm font-medium">No messages yet</p>
              <p className="text-gray-400 text-xs mt-1">
                {conversation ? 'Start the conversation below' : 'Send a message to negotiate with the host'}
              </p>
            </div>
          )}
          {chatMessages.map((msg) => {
            const isOwn = msg.senderId === currentUserId;
            return (
              <div key={msg._id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 ${
                  isOwn
                    ? 'bg-[#1F3A68] text-white rounded-tr-sm'
                    : 'bg-white text-gray-800 rounded-tl-sm shadow-sm'
                }`}>
                  {!isOwn && (
                    <p className="text-xs font-semibold text-[#1F3A68] mb-1">{msg.senderName}</p>
                  )}
                  <p className="text-sm leading-relaxed">{msg.content}</p>
                  <p className={`text-[10px] mt-1 ${isOwn ? 'text-white/60' : 'text-gray-400'}`}>
                    {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div className="border-t p-3 flex gap-2 bg-white rounded-b-xl flex-shrink-0">
          <input
            type="text"
            value={chatInput}
            onChange={e => setChatInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendMessage(); } }}
            placeholder="Type a message…"
            disabled={chatSending}
            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1F3A68]"
          />
          <Button
            size="sm"
            className="bg-[#1F3A68] hover:bg-[#1F3A68]/90 px-3"
            onClick={handleSendMessage}
            disabled={chatSending || !chatInput.trim()}
          >
            <Send size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default LandlordDashboard;
